import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Divider,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import DateField from './DateField';
import { formatDate } from '../utils/dateUtils';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

interface CatData {
  [key: string]: any;
}

interface MedicalEvent {
  id: number;
  event_type: string;
  event_date: string;
  cost: number;
  veterinary_name: string;
  invoice_file_path: string;
  notes: string;
}

interface CheckoutEvent {
  id: number;
  event_type: string;
  event_date: string;
  notes: string;
  created_at: string;
}

const CatFullRecord: React.FC = () => {
  const { t } = useTranslation();
  const { catId } = useParams<{ catId: string }>();
  const navigate = useNavigate();

  const [catData, setCatData] = useState<CatData | null>(null);
  const [medicalEvents, setMedicalEvents] = useState<MedicalEvent[]>([]);
  const [checkoutEvents, setCheckoutEvents] = useState<CheckoutEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Edit dialog state
  const [editCatOpen, setEditCatOpen] = useState(false);
  const [catFormData, setCatFormData] = useState<any>({});
  const [editPhotoFile, setEditPhotoFile] = useState<File | null>(null);
  const [municipalities, setMunicipalities] = useState<string[]>([]);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    loadCatData();
    loadMunicipalities();
  }, [catId]);

  const loadCatData = async () => {
    try {
      setLoading(true);

      // Load cat basic data
      const catResponse = await api.get(`${API_URL}/api/tables/public/cats/data?limit=1000`);
      const catColumns: string[] = catResponse.data.columns;
      const catRows = catResponse.data.rows;

      // Find the index of the 'id' column dynamically
      const catIdIndex = catColumns.indexOf('id');
      const cat = catRows.find((row: any[]) => row[catIdIndex] === parseInt(catId || '0'));

      if (!cat) {
        setError(t('catRecord.catNotFound'));
        setLoading(false);
        return;
      }

      // Convert row array to object
      const catObject: CatData = {};
      catColumns.forEach((col: string, index: number) => {
        catObject[col] = cat[index];
      });
      setCatData(catObject);

      // Load medical events
      const medicalResponse = await api.get(`${API_URL}/api/tables/public/medical/data?limit=1000`);
      const medicalColumns: string[] = medicalResponse.data.columns;
      const medicalRows = medicalResponse.data.rows;

      // Find the index of the 'cat_id' column dynamically
      const medicalCatIdIndex = medicalColumns.indexOf('cat_id');
      const catMedicalEvents = medicalRows
        .filter((row: any[]) => row[medicalCatIdIndex] === parseInt(catId || '0'))
        .map((row: any[]) => {
          const event: any = {};
          medicalColumns.forEach((col: string, index: number) => {
            event[col] = row[index];
          });
          return event;
        });
      setMedicalEvents(catMedicalEvents);

      // Load checkout events
      try {
        const checkoutResponse = await api.get(`${API_URL}/api/tables/public/checkout_events/data?limit=1000`);
        const checkoutColumns: string[] = checkoutResponse.data.columns;
        const checkoutRows = checkoutResponse.data.rows;

        // Find the index of the 'cat_id' column dynamically
        const checkoutCatIdIndex = checkoutColumns.indexOf('cat_id');
        const catCheckoutEvents = checkoutRows
          .filter((row: any[]) => row[checkoutCatIdIndex] === parseInt(catId || '0'))
          .map((row: any[]) => {
            const event: any = {};
            checkoutColumns.forEach((col: string, index: number) => {
              event[col] = row[index];
            });
            return event;
          });
        setCheckoutEvents(catCheckoutEvents);
      } catch (err) {
        // Checkout events might not exist for this cat
        setCheckoutEvents([]);
      }

      setLoading(false);
    } catch (error: any) {
      setError(`Failed to load cat data: ${error.message}`);
      setLoading(false);
    }
  };

  const loadMunicipalities = async () => {
    try {
      const response = await api.get(`${API_URL}/api/tables/public/municipalities/data?limit=1000`);
      const municipalitiesData = response.data.rows.map((row: any[]) => row[0]);
      setMunicipalities(municipalitiesData.sort());
    } catch (error) {
      console.error('Error loading municipalities:', error);
    }
  };


  const handleUpdateCat = async () => {
    try {
      let updatedData = { ...catFormData };
      if (updatedData.sterilized === 'U') updatedData.sterilized = null;

      // Upload photo if a new one is selected
      if (editPhotoFile) {
        const formData = new FormData();
        formData.append('file', editPhotoFile);

        const uploadResponse = await api.post(`/api/upload/photo`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        updatedData.photo_file_path = uploadResponse.data.file_path;
      }

      await api.put(`/api/records`, {
        table: 'cats',
        schema: 'public',
        record_id: catData?.id,
        data: updatedData,
      });

      // Upsert a cost-class history entry for today (insert or update if already exists)
      const oldCostClass = catData?.cost_class || 'Ordinary';
      const newCostClass = updatedData.cost_class || 'Ordinary';
      if (newCostClass !== oldCostClass) {
        const todayStr = new Date().toISOString().split('T')[0];
        await api.post(`/api/cost-class-history/cost_class_history/cat_id/${catData?.id}/upsert`, {
          cost_class: newCostClass,
          effective_from: todayStr,
        });
      }

      setMessage({ type: 'success', text: t('catRecord.catUpdated') });
      setEditCatOpen(false);
      setEditPhotoFile(null);
      // Reload cat data to show updates
      loadCatData();
      setTimeout(() => setMessage(null), 3000);
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to update cat';
      setMessage({ type: 'error', text: errorMessage });
      setTimeout(() => setMessage(null), 5000);
    }
  };

  const handleExportPDF = () => {
    if (!catData) return;

    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      let yPos = 20;

      // Title
      doc.setFontSize(20);
      doc.setTextColor(102, 126, 234);
      doc.text(t('catRecord.title'), pageWidth / 2, yPos, { align: 'center' });
      yPos += 15;

      // Cat Name and Chip ID
      doc.setFontSize(16);
      doc.setTextColor(0, 0, 0);
      const catName = catData.name || t('cat.unnamed');
      const chipInfo = catData.chip_id ? ` (${t('catFields.chipId')}: ${catData.chip_id})` : '';
      doc.text(`${catName}${chipInfo}`, pageWidth / 2, yPos, { align: 'center' });
      yPos += 15;

      // Basic Information Section
      doc.setFontSize(14);
      doc.setTextColor(102, 126, 234);
      doc.text(t('catRecord.basicInfo'), 14, yPos);
      yPos += 8;

      // One sub-table per info group (same structure as the on-screen view).
      const pageHeight = doc.internal.pageSize.getHeight();
      infoGroups.forEach((group) => {
        const fields = group.fields.filter(hasValue);
        if (fields.length === 0) return;

        // Keep the group heading with at least its first row on the same page.
        if (yPos + 16 > pageHeight - 20) { doc.addPage(); yPos = 20; }

        doc.setFontSize(11);
        doc.setTextColor(102, 126, 234);
        doc.text(group.title, 14, yPos);
        yPos += 4;

        autoTable(doc, {
          startY: yPos,
          body: fields.map((key) => [fieldLabel(key), fieldDisplay(key, (catData as any)[key])]),
          theme: 'grid',
          styles: { fontSize: 9 },
          columnStyles: { 0: { fontStyle: 'bold', cellWidth: 60, textColor: [80, 80, 80] } },
          margin: { left: 14, right: 14 },
        });

        yPos = (doc as any).lastAutoTable.finalY + 8;
      });

      yPos += 2;

      // Medical Events Section
      if (medicalEvents.length > 0) {
        doc.setFontSize(14);
        doc.setTextColor(102, 126, 234);
        doc.text(`${t('catRecord.medicalEvents')} (${medicalEvents.length})`, 14, yPos);
        yPos += 7;

        const medicalData = medicalEvents.map(event => [
          translateEventType(event.event_type),
          event.event_date ? formatDate(event.event_date) : t('common.na'),
          event.cost ? `€${event.cost.toFixed(2)}` : t('common.na'),
          event.veterinary_name || t('common.na'),
          event.notes || t('common.na'),
        ]);

        autoTable(doc, {
          startY: yPos,
          head: [[t('medical.eventType'), t('common.date'), t('medical.costEuro'), t('medical.veterinary'), t('cat.notes')]],
          body: medicalData,
          theme: 'grid',
          headStyles: { fillColor: [102, 126, 234], textColor: 255 },
          margin: { left: 14, right: 14 },
        });

        yPos = (doc as any).lastAutoTable.finalY + 10;
      } else {
        doc.setFontSize(10);
        doc.setTextColor(100, 100, 100);
        doc.text(t('catRecord.noMedicalEvents'), 14, yPos);
        yPos += 10;
      }

      // Checkout Events Section
      if (checkoutEvents.length > 0) {
        // Add new page if needed
        if (yPos > 250) {
          doc.addPage();
          yPos = 20;
        }

        doc.setFontSize(14);
        doc.setTextColor(102, 126, 234);
        doc.text(`${t('catRecord.checkoutEvents')} (${checkoutEvents.length})`, 14, yPos);
        yPos += 7;

        const checkoutData = checkoutEvents.map(event => [
          translateCheckoutType(event.event_type),
          event.event_date ? formatDate(event.event_date) : t('common.na'),
          event.notes || t('common.na'),
          event.created_at || t('common.na'),
        ]);

        autoTable(doc, {
          startY: yPos,
          head: [[t('medical.eventType'), t('common.date'), t('cat.notes'), t('common.createdAt')]],
          body: checkoutData,
          theme: 'grid',
          headStyles: { fillColor: [102, 126, 234], textColor: 255 },
          margin: { left: 14, right: 14 },
        });
      }

      // Save the PDF
      doc.save(`cat_${catId}_record.pdf`);
    } catch (error: any) {
      alert(`Failed to export PDF: ${error.message}`);
    }
  };

  const formatFieldName = (fieldName: string): string => {
    return fieldName
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const translateCheckoutType = (et: string | null | undefined): string => {
    if (!et) return t('common.na');
    const map: Record<string, string> = {
      'Death': t('catStatus.deceased'),
      'Adoption': t('catStatus.adopted'),
      'Freed': t('catStatus.freed'),
    };
    return map[et] || et;
  };

  const translateEventType = (et: string | null | undefined): string => {
    if (!et) return t('common.na');
    const map: Record<string, string> = {
      'Vaccine': t('medical.eventTypes.vaccine'),
      'Sterilization': t('medical.eventTypes.sterilization'),
      'Treatment': t('medical.eventTypes.treatment'),
      'Delivery': t('medical.eventTypes.delivery'),
      'Surgery': t('medical.eventTypes.surgery'),
      'Checkup': t('medical.eventTypes.checkup'),
      'test_fiv_felv': t('medical.eventTypes.testFivFelv'),
      'Travel Expenses': t('medical.eventTypes.travelExpenses'),
      'Other': t('medical.eventTypes.other'),
    };
    return map[et] || et;
  };

  // Localized label for a cat field (used by the structured record view).
  const fieldLabel = (key: string): string => {
    const map: Record<string, string> = {
      name: t('catFields.name'), chip_id: t('catFields.chipId'), sex: t('catFields.sex'),
      age: t('catFields.age'), breed: t('catFields.breed'), color: t('catFields.color'),
      status: t('report.status'), external: t('report.external'),
      source: t('catFields.source'), municipality: t('catFields.municipality'),
      area: t('catFields.area'), address_where_found: t('catFields.addressWhereFound'),
      temporary_placement: t('catFields.temporaryPlacement'), referente_di_colonia: t('catFields.referenteDiColonia'),
      sterilized: t('catFields.sterilized'), fiv: t('catFields.fiv'), felv: t('catFields.felv'),
      initial_health_status: t('catFields.initialHealthStatus'), cost_class: t('cat.costClass'),
      arrival_date: t('catFields.arrivalDate'), departure_date: t('catFields.departureDate'),
      notes: t('catFields.notes'),
    };
    return map[key] || formatFieldName(key);
  };

  // Localized value for a cat field.
  const fieldDisplay = (key: string, value: any): string => {
    if (key === 'chip_id') return value ? String(value) : t('cat.missing');
    if (value === null || value === undefined || value === '') return t('common.na');
    switch (key) {
      case 'sterilized': return value === 'Y' ? t('common.yes') : value === 'N' ? t('common.no') : t('common.unknown');
      case 'fiv': case 'felv': return value === 'Y' ? t('common.positive') : value === 'N' ? t('common.negative') : t('common.unknown');
      case 'external': return value ? t('common.yes') : t('common.no');
      case 'arrival_date': case 'departure_date': return formatDate(String(value).slice(0, 10));
      case 'source': {
        const m: Record<string, string> = { ASL: 'asl', Private: 'private', Municipality: 'municipality', 'Referente di Colonia': 'referenteDiColonia' };
        return m[value] ? t(`catSources.${m[value]}`) : String(value);
      }
      case 'status': {
        const m: Record<string, string> = { active: 'active', adopted: 'adopted', freed: 'freed', angel: 'deceased' };
        return m[value] ? t(`catStatus.${m[value]}`) : String(value);
      }
      case 'cost_class': {
        const m: Record<string, string> = { Ordinary: 'ordinary', Increased: 'increased', 'Increased Kitten': 'increasedKitten', 'Increased Sickroom': 'increasedSickroom', Other: 'other' };
        return m[value] ? t(`cat.costClasses.${m[value]}`) : String(value);
      }
      default: return String(value);
    }
  };

  // Info grouped by type; a field is shown only when it has a value (chip is
  // always shown so a missing microchip reads as "Mancante").
  const infoGroups: { title: string; fields: string[] }[] = [
    { title: t('catRecord.identity'), fields: ['name', 'chip_id', 'sex', 'age', 'breed', 'color', 'status', 'external'] },
    { title: t('catRecord.locationSource'), fields: ['source', 'municipality', 'area', 'address_where_found', 'temporary_placement', 'referente_di_colonia'] },
    { title: t('catRecord.health'), fields: ['sterilized', 'fiv', 'felv', 'initial_health_status', 'cost_class'] },
    { title: t('catRecord.dates'), fields: ['arrival_date', 'departure_date'] },
    { title: t('catFields.notes'), fields: ['notes'] },
  ];
  const hasValue = (key: string): boolean => {
    if (key === 'chip_id') return true;
    if (key === 'external') return !!(catData && (catData as any).external);
    const v = catData ? (catData as any)[key] : undefined;
    return v !== null && v !== undefined && v !== '';
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error || !catData) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{error || t('catRecord.catNotFound')}</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/')}
          sx={{ mt: 2 }}
        >
          {t('catRecord.backToMainMenu')}
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper
        elevation={0}
        sx={{
          p: 4,
          borderRadius: 3,
          background: 'white',
          boxShadow: '0 8px 32px rgba(102,126,234,0.15)',
        }}
      >
        {/* Header with Photo */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: '#667eea', mb: 1 }}>
              {t('catRecord.title')}
            </Typography>
            <Typography variant="h5" color="text.secondary">
              {catData.name || t('cat.unnamed')} {catData.chip_id ? `(${t('catFields.chipId')}: ${catData.chip_id})` : ''}
            </Typography>
          </Box>

          {catData.photo_file_path && (
            <Box
              component="img"
              src={`${API_URL}${catData.photo_file_path}`}
              alt={catData.name || 'Cat photo'}
              sx={{
                width: 200,
                height: 200,
                borderRadius: 3,
                objectFit: 'cover',
                border: '3px solid #667eea',
                boxShadow: '0 4px 12px rgba(102,126,234,0.3)',
              }}
            />
          )}
        </Box>

        {/* Message Alert */}
        {message && (
          <Alert severity={message.type} sx={{ mb: 2 }}>
            {message.text}
          </Alert>
        )}

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/')}
            variant="outlined"
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            {t('catRecord.backToMainMenu')}
          </Button>

          <Button
            startIcon={<PdfIcon />}
            onClick={handleExportPDF}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(102,126,234,0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
              }
            }}
          >
            {t('catRecord.exportPdf')}
          </Button>
        </Box>

        <Divider sx={{ mb: 4 }} />

        {/* Cat Information — grouped by type */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#667eea', mb: 2 }}>
            {t('catRecord.basicInfo')}
          </Typography>
          <Grid container spacing={2}>
            {infoGroups.map((group) => {
              const fields = group.fields.filter(hasValue);
              if (fields.length === 0) return null;
              return (
                <Grid item xs={12} md={6} key={group.title}>
                  <Box sx={{ p: 2, bgcolor: '#f9f9f9', borderRadius: 2, height: '100%' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#667eea', mb: 1 }}>
                      {group.title}
                    </Typography>
                    {fields.map((key) => (
                      <Box
                        key={key}
                        sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.4, borderBottom: '1px solid rgba(0,0,0,0.06)' }}
                      >
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, flexShrink: 0 }}>
                          {fieldLabel(key)}
                        </Typography>
                        <Typography variant="body2" sx={{ textAlign: 'right', wordBreak: 'break-word' }}>
                          {fieldDisplay(key, (catData as any)[key])}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Box>

        <Divider sx={{ mb: 4 }} />

        {/* Medical Events */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#667eea', mb: 2 }}>
            {t('catRecord.medicalEvents')} ({medicalEvents.length})
          </Typography>
          {medicalEvents.length > 0 ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'primary.light' }}>
                    <TableCell sx={{ fontWeight: 600, color: 'white' }}>{t('medical.eventType')}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'white' }}>{t('common.date')}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'white' }}>{t('medical.costEuro')}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'white' }}>{t('medical.veterinary')}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'white' }}>{t('cat.notes')}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'white' }}>{t('medical.invoice')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {medicalEvents.map((event, index) => (
                    <TableRow key={index} hover>
                      <TableCell>{translateEventType(event.event_type)}</TableCell>
                      <TableCell>{formatDate(event.event_date)}</TableCell>
                      <TableCell>€{event.cost ? event.cost.toFixed(2) : t('common.na')}</TableCell>
                      <TableCell>{event.veterinary_name || t('common.na')}</TableCell>
                      <TableCell>{event.notes || t('common.na')}</TableCell>
                      <TableCell>
                        {event.invoice_file_path ? (
                          <a href={`${API_URL}${event.invoice_file_path}`} target="_blank" rel="noopener noreferrer">
                            {t('catRecord.viewInvoice')}
                          </a>
                        ) : (
                          t('common.na')
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography color="text.secondary">{t('catRecord.noMedicalEvents')}</Typography>
          )}
        </Box>

        <Divider sx={{ mb: 4 }} />

        {/* Checkout Events */}
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#667eea', mb: 2 }}>
            {t('catRecord.checkoutEvents')} ({checkoutEvents.length})
          </Typography>
          {checkoutEvents.length > 0 ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'primary.light' }}>
                    <TableCell sx={{ fontWeight: 600, color: 'white' }}>{t('medical.eventType')}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'white' }}>{t('common.date')}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'white' }}>{t('cat.notes')}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'white' }}>{t('common.createdAt')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {checkoutEvents.map((event, index) => (
                    <TableRow key={index} hover>
                      <TableCell>{translateCheckoutType(event.event_type)}</TableCell>
                      <TableCell>{formatDate(event.event_date)}</TableCell>
                      <TableCell>{event.notes || t('common.na')}</TableCell>
                      <TableCell>{event.created_at}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography color="text.secondary">{t('catRecord.noCheckoutEvents')}</Typography>
          )}
        </Box>
      </Paper>

      {/* Edit Cat Dialog */}
      <Dialog
        open={editCatOpen}
        onClose={() => {
          setEditCatOpen(false);
          setEditPhotoFile(null);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(102,126,234,0.25)',
          }
        }}
      >
        <DialogTitle sx={{ pb: 1, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', fontWeight: 700 }}>
          {t('catRecord.editCat')}: {catData?.name || catData?.chip_id}
        </DialogTitle>
        <DialogContent sx={{ pt: '12px !important', pb: 1, px: 2 }}>
          {/* Identity Section */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#667eea', mb: 0.75, fontSize: '0.8rem' }}>
            {t('catRecord.identity')}
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label={t('catFields.chipId')} value={catFormData.chip_id || ''} onChange={(e) => setCatFormData({ ...catFormData, chip_id: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label={t('catFields.name')} value={catFormData.name || ''} onChange={(e) => setCatFormData({ ...catFormData, name: e.target.value })} />
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('catFields.sex')}</InputLabel>
                <Select value={catFormData.sex || ''} label={t('catFields.sex')} onChange={(e) => setCatFormData({ ...catFormData, sex: e.target.value })}>
                  <MenuItem value=""><em>{t('common.none')}</em></MenuItem>
                  <MenuItem value="M">M</MenuItem>
                  <MenuItem value="F">F</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={8}>
              {(() => {
                const ageValue = catFormData.age || '';
                const parts = ageValue.split('/');
                const month = parts[0] || '';
                const year = parts[1] || '';
                return (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <FormControl sx={{ width: '45%' }} size="small">
                      <InputLabel>{t('catFields.birthMonth')}</InputLabel>
                      <Select
                        value={month}
                        label={t('catFields.birthMonth')}
                        onChange={(e) => setCatFormData({ ...catFormData, age: `${e.target.value}/${year}` })}
                      >
                        <MenuItem value=""><em>{t('catFields.birthMonth')}</em></MenuItem>
                        {['01','02','03','04','05','06','07','08','09','10','11','12'].map(m => (
                          <MenuItem key={m} value={m}>{m}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      sx={{ width: '55%' }}
                      size="small"
                      label={t('catFields.birthYear')}
                      placeholder="YYYY"
                      value={year}
                      onChange={(e) => {
                        const newYear = e.target.value.replace(/\D/g, '').slice(0, 4);
                        setCatFormData({ ...catFormData, age: `${month}/${newYear}` });
                      }}
                      inputProps={{ maxLength: 4 }}
                    />
                  </Box>
                );
              })()}
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label={t('catFields.breed')} value={catFormData.breed || ''} onChange={(e) => setCatFormData({ ...catFormData, breed: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label={t('catFields.color')} value={catFormData.color || ''} onChange={(e) => setCatFormData({ ...catFormData, color: e.target.value })} />
            </Grid>
          </Grid>

          <Divider sx={{ my: 1 }} />

          {/* Location & Source Section */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#667eea', mb: 0.75, fontSize: '0.8rem' }}>
            {t('catRecord.locationSource')}
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('catFields.source')}</InputLabel>
                <Select value={catFormData.source || ''} label={t('catFields.source')} onChange={(e) => setCatFormData({ ...catFormData, source: e.target.value })}>
                  <MenuItem value=""><em>{t('common.none')}</em></MenuItem>
                  <MenuItem value="ASL">{t('catSources.asl')}</MenuItem>
                  <MenuItem value="Private">{t('catSources.private')}</MenuItem>
                  <MenuItem value="Municipality">{t('catSources.municipality')}</MenuItem>
                  <MenuItem value="Referente di Colonia">{t('catSources.referenteDiColonia')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('catFields.municipality')}</InputLabel>
                <Select value={catFormData.municipality || ''} label={t('catFields.municipality')} onChange={(e) => setCatFormData({ ...catFormData, municipality: e.target.value })}>
                  <MenuItem value=""><em>{t('common.none')}</em></MenuItem>
                  {municipalities.map((m: string) => (
                    <MenuItem key={m} value={m}>{m}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label={t('catFields.referenteDiColonia')} value={catFormData.referente_di_colonia || ''} onChange={(e) => setCatFormData({ ...catFormData, referente_di_colonia: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label={t('catFields.addressWhereFound')} value={catFormData.address_where_found || ''} onChange={(e) => setCatFormData({ ...catFormData, address_where_found: e.target.value })} multiline rows={2} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label={t('catFields.temporaryPlacement')} value={catFormData.temporary_placement || ''} onChange={(e) => setCatFormData({ ...catFormData, temporary_placement: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label={t('catFields.area')} value={catFormData.area || ''} onChange={(e) => setCatFormData({ ...catFormData, area: e.target.value })} />
            </Grid>
          </Grid>

          <Divider sx={{ my: 1 }} />

          {/* Health Section */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#667eea', mb: 0.75, fontSize: '0.8rem' }}>
            {t('catRecord.health')}
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label={t('catFields.initialHealthStatus')} value={catFormData.initial_health_status || ''} onChange={(e) => setCatFormData({ ...catFormData, initial_health_status: e.target.value })} />
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('catFields.sterilized')}</InputLabel>
                <Select value={catFormData.sterilized ?? 'U'} label={t('catFields.sterilized')} onChange={(e: any) => setCatFormData({ ...catFormData, sterilized: e.target.value })}>
                  <MenuItem value="U">{t('common.unknown')}</MenuItem>
                  <MenuItem value="N">N</MenuItem>
                  <MenuItem value="Y">Y</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('catFields.fiv')}</InputLabel>
                <Select value={catFormData.fiv || 'Unknown'} label={t('catFields.fiv')} onChange={(e) => setCatFormData({ ...catFormData, fiv: e.target.value })}>
                  <MenuItem value="Unknown">{t('common.unknown')}</MenuItem>
                  <MenuItem value="Y">Y</MenuItem>
                  <MenuItem value="N">N</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('catFields.felv')}</InputLabel>
                <Select value={catFormData.felv || 'Unknown'} label={t('catFields.felv')} onChange={(e) => setCatFormData({ ...catFormData, felv: e.target.value })}>
                  <MenuItem value="Unknown">{t('common.unknown')}</MenuItem>
                  <MenuItem value="Y">Y</MenuItem>
                  <MenuItem value="N">N</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small" required>
                <InputLabel>{t('cat.costClass')}</InputLabel>
                <Select value={catFormData.cost_class || 'Ordinary'} label={t('cat.costClass')} onChange={(e: any) => setCatFormData({ ...catFormData, cost_class: e.target.value })}>
                  <MenuItem value="Ordinary">{t('cat.costClasses.ordinary')}</MenuItem>
                  <MenuItem value="Increased">{t('cat.costClasses.increased')}</MenuItem>
                  <MenuItem value="Increased Kitten">{t('cat.costClasses.increasedKitten')}</MenuItem>
                  <MenuItem value="Increased Sickroom">{t('cat.costClasses.increasedSickroom')}</MenuItem>
                  <MenuItem value="Other">{t('cat.costClasses.other')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Divider sx={{ my: 1 }} />

          {/* Dates Section */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#667eea', mb: 0.75, fontSize: '0.8rem' }}>
            {t('catRecord.dates')}
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <DateField label={t('catFields.arrivalDate')} value={catFormData.arrival_date || ''} onChange={(v: string) => setCatFormData({ ...catFormData, arrival_date: v })} />
            </Grid>
            <Grid item xs={6}>
              <DateField label={t('catFields.departureDate')} value={catFormData.departure_date || ''} onChange={(v: string) => setCatFormData({ ...catFormData, departure_date: v })} />
            </Grid>
          </Grid>

          <Divider sx={{ my: 1 }} />

          {/* Notes & Photo Section */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#667eea', mb: 0.75, fontSize: '0.8rem' }}>
            {t('catRecord.notesPhoto')}
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label={t('cat.notes')} value={catFormData.notes || ''} onChange={(e) => setCatFormData({ ...catFormData, notes: e.target.value })} multiline rows={2} />
            </Grid>
            <Grid item xs={12}>
              {/* Photo section */}
              {catFormData.photo_file_path && !editPhotoFile && (
                <Box sx={{ mb: 1.5 }}>
                  <Box
                    component="img"
                    src={`${API_URL}${catFormData.photo_file_path}`}
                    alt="Cat photo"
                    sx={{ width: '100%', maxWidth: 200, height: 'auto', borderRadius: 2, border: '2px solid #667eea', mb: 0.5 }}
                  />
                  <Typography variant="caption" color="text.secondary" display="block">{t('catRecord.currentPhoto')}</Typography>
                </Box>
              )}
              {editPhotoFile && (
                <Box sx={{ mb: 1.5 }}>
                  <Box
                    component="img"
                    src={URL.createObjectURL(editPhotoFile)}
                    alt="New cat photo"
                    sx={{ width: '100%', maxWidth: 200, height: 'auto', borderRadius: 2, border: '2px solid #4caf50', mb: 0.5 }}
                  />
                  <Typography variant="caption" color="success.main" display="block" fontWeight="bold">{t('catRecord.newPhotoSelected')}</Typography>
                  <Typography variant="caption" color="text.secondary" display="block">{editPhotoFile.name} ({(editPhotoFile.size / 1024).toFixed(2)} KB)</Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button variant="contained" component="label" size="small" sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', textTransform: 'none', fontWeight: 600, flexGrow: 1 }}>
                  {editPhotoFile ? t('catRecord.changePhoto') : catFormData.photo_file_path ? t('catRecord.replacePhoto') : t('catRecord.uploadPhoto')}
                  <input type="file" hidden accept="image/*" onChange={(e) => { if (e.target.files && e.target.files[0]) setEditPhotoFile(e.target.files[0]); }} />
                </Button>
                {editPhotoFile && (
                  <Button variant="outlined" color="error" size="small" onClick={() => setEditPhotoFile(null)} sx={{ textTransform: 'none', fontWeight: 600 }}>
                    {t('common.clear')}
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setEditCatOpen(false);
              setEditPhotoFile(null);
            }}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleUpdateCat}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(102,126,234,0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
              }
            }}
          >
            {t('common.update')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CatFullRecord;
